import {
  EMemberCenterErrorCode,
  RESEND_VERIFICATION_CODE_COOLDOWN_MS,
} from 'constants/memberCenter';
import { EResetPasswordPlatform } from 'types/memberCenter';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Mui } from '@onedesign/ui';
import { Icon, SvgName } from 'components/Icon';
import axios from 'plugins/api/axios';
import { useLoadingService } from 'services/LoadingService';
import { PATH } from 'routes/routeConfig';
import { t } from 'utils/i18n';
import { Dialog } from 'components/Dialog';
import VerificationCodeForm from 'components/VerificationCodeForm';
import {
  StyledBackButton,
  StyledContainerMemberInfo,
  StyledHeaderConfirm,
  StyledHeaderConfirmTitle,
  StyledFormContainerMemberConfirm,
} from 'pages/Login/Login.style';
import { StyledFormControlResetPassword } from './ResetPassword.style';

const { memberCenter } = axios;
const { Button, TextField, FormHelperText, IconButton, InputAdornment } = Mui;

const passwordRegex = /^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z\d]{8,12}$/;

type TResetPasswordResult = 'success' | 'failed' | null;

interface IResetPasswordForm {
  password: string;
  confirmPassword: string;
}

const ResetPassword = () => {
  const passwordFormatMessage = t(
    'resetPassword.validation.passwordFormatInvalid',
    '請輸入8-12位元英數混和密碼',
  );

  const schema = yup
    .object({
      password: yup
        .string()
        .required(t('resetPassword.validation.passwordRequired', '密碼未輸入'))
        .matches(passwordRegex, passwordFormatMessage)
        .min(8, passwordFormatMessage)
        .max(12, passwordFormatMessage),
      confirmPassword: yup
        .string()
        .required(t('resetPassword.validation.passwordRequired', '密碼未輸入'))
        .test(
          'confirm-password-not-match',
          t(
            'resetPassword.validation.passwordMismatch',
            '密碼不一致，請重新輸入',
          ),
          (value, context) => {
            if (!value) return true;
            return value === context.parent.password;
          },
        ),
    })
    .required();

  const [searchParams] = useSearchParams();
  const [result, setResult] = useState<TResetPasswordResult>(null);
  const [passwordShow, setPasswordShow] = useState(false);
  const [confirmPasswordShow, setConfirmPasswordShow] = useState(false);
  const [failureReason, setFailureReason] = useState<
    'expired' | 'locked' | 'generic' | null
  >(null);
  const { showLoading, hideLoading } = useLoadingService();
  const navigate = useNavigate();

  const account = searchParams.get('account') ?? '';
  const code = searchParams.get('code') ?? '';
  const email = searchParams.get('email') ?? '';
  const resendAvailableAt = Number(searchParams.get('resendAvailableAt')) || 0;

  const methods = useForm<IResetPasswordForm>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = methods;

  const handleSubmitForm = async ({ password }: IResetPasswordForm) => {
    showLoading();
    const res = await memberCenter.completeResetPassword({
      account,
      code,
      password,
    });
    hideLoading();

    if (res.err === EMemberCenterErrorCode.SUCCESS && res.data?.success) {
      setResult('success');
      return;
    }
    if (res.err === EMemberCenterErrorCode.VERIFICATION_CODE_EXPIRED) {
      setFailureReason('expired');
    } else if (res.err === EMemberCenterErrorCode.VERIFICATION_CODE_LOCKED) {
      setFailureReason('locked');
    } else {
      setFailureReason('generic');
    }
    setResult('failed');
  };

  const handleVerifyCode = async (inputCode: string) => {
    showLoading();
    const res = await memberCenter.checkResetPassword({
      account,
      code: inputCode,
    });
    hideLoading();

    if (res.err === EMemberCenterErrorCode.SUCCESS && res.data?.isValid) {
      const params = new URLSearchParams({
        account,
        email,
        code: inputCode,
        resendAvailableAt: String(resendAvailableAt),
      });
      navigate(`${PATH.RESET_PASSWORD}?${params.toString()}`, {
        replace: true,
      });
      return 'success';
    }
    if (res.err === EMemberCenterErrorCode.VERIFICATION_CODE_EXPIRED) {
      return 'expired';
    }
    if (res.err === EMemberCenterErrorCode.VERIFICATION_CODE_LOCKED) {
      return 'locked';
    }
    return 'invalid';
  };

  const handleResend = async () => {
    showLoading();
    const res = await memberCenter.initiateResetPassword({
      account,
      platform: EResetPasswordPlatform.EMAIL,
    });
    hideLoading();

    if (res.err === EMemberCenterErrorCode.SUCCESS) {
      const params = new URLSearchParams({
        account,
        email: res.data?.email ?? email,
        resendAvailableAt: String(
          Date.now() + RESEND_VERIFICATION_CODE_COOLDOWN_MS,
        ),
      });
      navigate(`${PATH.RESET_PASSWORD}?${params.toString()}`, {
        replace: true,
      });
      return true;
    }
    return false;
  };

  if (!code) {
    return (
      <VerificationCodeForm
        title={t('resetPassword.title', '重設密碼')}
        description={t(
          'resetPassword.verifyCodeSent',
          '我們已寄送一組驗證碼至 {{email}}，請輸入驗證碼完成身份驗證。',
          { email },
        )}
        resendAvailableAt={resendAvailableAt}
        onBack={() => navigate(PATH.RESET_PASSWORD_REQUEST)}
        onSubmit={handleVerifyCode}
        onResend={handleResend}
      />
    );
  }

  const handleBackToVerifyCode = () => {
    const params = new URLSearchParams({
      account,
      email,
      resendAvailableAt: String(resendAvailableAt),
    });
    navigate(`${PATH.RESET_PASSWORD}?${params.toString()}`);
  };

  const dialogContentByKey: Record<
    'success' | 'expired' | 'locked' | 'generic',
    { title: string; hint: string; buttonText: string }
  > = {
    success: {
      title: t('resetPassword.successTitle', '密碼重設成功'),
      hint: t('resetPassword.successHint', '請使用新密碼重新登入。'),
      buttonText: t('resetPassword.confirmButton', '確認'),
    },
    expired: {
      title: t('resetPassword.expiredTitle', '重設密碼已逾時'),
      hint: t(
        'resetPassword.expiredHint',
        '驗證碼已逾時，請重新申請重設密碼。',
      ),
      buttonText: t('resetPassword.goToHome', '回首頁'),
    },
    locked: {
      title: t('resetPassword.lockedTitle', '驗證碼已被鎖定'),
      hint: t(
        'resetPassword.lockedHint',
        '此驗證碼已被鎖定，請重新申請重設密碼。',
      ),
      buttonText: t('resetPassword.goToHome', '回首頁'),
    },
    generic: {
      title: t('resetPassword.invalidTitle', '無法重設密碼'),
      hint: t('resetPassword.failedHint', '請重新申請重設密碼。'),
      buttonText: t('resetPassword.goToHome', '回首頁'),
    },
  };

  const {
    title: dialogTitle,
    hint: dialogHint,
    buttonText: dialogButtonText,
  } = dialogContentByKey[
    result === 'success' ? 'success' : failureReason ?? 'generic'
  ];

  return (
    <>
      <StyledContainerMemberInfo>
        <StyledBackButton
          color="inherit"
          startIcon={
            <Icon
              sx={{ width: '18px', height: '18px' }}
              name={SvgName.ChevronLeft}
            />
          }
          onClick={handleBackToVerifyCode}
        >
          {t('resetPassword.backToVerifyCode', '返回')}
        </StyledBackButton>
        <StyledHeaderConfirm>
          <StyledHeaderConfirmTitle>
            {t('resetPassword.title', '重設密碼')}
          </StyledHeaderConfirmTitle>
        </StyledHeaderConfirm>
        <FormProvider {...methods}>
          <StyledFormContainerMemberConfirm>
            <form>
              <StyledFormControlResetPassword>
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <>
                      <TextField
                        {...field}
                        type={passwordShow ? 'text' : 'password'}
                        placeholder={t(
                          'resetPassword.passwordPlaceholder',
                          '新密碼',
                        )}
                        fullWidth
                        error={!!errors.password}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={() => setPasswordShow(show => !show)}
                                edge="end"
                              >
                                {passwordShow ? (
                                  <Icon name={SvgName.EyeOn} />
                                ) : (
                                  <Icon name={SvgName.EyeOff} />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      {errors.password && (
                        <FormHelperText
                          sx={{
                            color: theme => theme.palette.error.main,
                          }}
                        >
                          {errors.password.message}
                        </FormHelperText>
                      )}
                    </>
                  )}
                />
              </StyledFormControlResetPassword>
              <StyledFormControlResetPassword>
                <Controller
                  name="confirmPassword"
                  control={control}
                  render={({ field }) => (
                    <>
                      <TextField
                        {...field}
                        type={confirmPasswordShow ? 'text' : 'password'}
                        placeholder={t(
                          'resetPassword.confirmPasswordPlaceholder',
                          '確認新密碼',
                        )}
                        fullWidth
                        error={!!errors.confirmPassword}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={() =>
                                  setConfirmPasswordShow(show => !show)
                                }
                                edge="end"
                              >
                                {confirmPasswordShow ? (
                                  <Icon name={SvgName.EyeOn} />
                                ) : (
                                  <Icon name={SvgName.EyeOff} />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      {errors.confirmPassword && (
                        <FormHelperText
                          sx={{
                            color: theme => theme.palette.error.main,
                          }}
                        >
                          {errors.confirmPassword.message}
                        </FormHelperText>
                      )}
                    </>
                  )}
                />
              </StyledFormControlResetPassword>
              <Button
                type="submit"
                sx={{ padding: '12px 0', marginTop: '2rem' }}
                variant="contained"
                onClick={handleSubmit(handleSubmitForm)}
              >
                {t('resetPassword.submitButton', '送出')}
              </Button>
            </form>
          </StyledFormContainerMemberConfirm>
        </FormProvider>
      </StyledContainerMemberInfo>
      <Dialog
        open={result !== null}
        title={dialogTitle}
        close={{ show: false, action: () => {} }}
        confirm={{
          text: dialogButtonText,
          show: true,
          action: () => navigate(PATH.LOGIN, { replace: true }),
        }}
      >
        {dialogHint}
      </Dialog>
    </>
  );
};

export default ResetPassword;
